import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { timeout } from 'rxjs/operators';
import { ObjectSerializer } from 'DiscoveryEngine.TS/Serialization';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';


@Injectable()
export class UvExplorerClient {

	// Needed to void circular dependency between UvExplorerClient and IdentityService
	public static clearSession: () => void;

	private static readonly DefaultHttpRequestTimeoutMs = 24 * 60 * 60 * 1000; // one day

	constructor(private http: HttpClient) {
	}

	private handleUnauthorized<T>(subject: Subject<T>, observable: Observable<T>): Subscription {
		return observable.subscribe(
			(result: T) => {
				subject.next(result);
			},
			(error: HttpErrorResponse) => {
				let errMsg: string = this.makeErrorMessage(error);
				subject.error(errMsg);
				if (!!error.status && error.status === 401) {
					UvExplorerClient.clearSession();
				}
			});
	}

	public get<T>(url: string, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs) : Observable<T> {
		let subject = new Subject<T>();

		var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.get<T>(url, requestOptions).pipe(timeout(timeoutMs)));

		return subject.asObservable();
	}

	public getObjSer<T extends ObjectSerializer>(url: string, ctor: new () => T, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

		var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.get<T>(url, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: T) => this.extractObjSerData(res, ctor)));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public post<T>(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs) : Observable<T> {
		let subject = new Subject<T>();

        var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.post<T>(url, body, requestOptions).pipe(timeout(timeoutMs)));

		return subject.asObservable();
	}

	public postWithBinaryResult(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<Blob> {
		let subject = new Subject<Blob>();

        var requestOptions = this.binaryRequestOptions();
		this.handleUnauthorized(subject, this.http.post<Blob>(url, body, requestOptions).pipe(timeout(timeoutMs)));

		return subject.asObservable();
	}

	public postWithTextResult(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<string> {
		let subject = new Subject<string>();

		var requestOptions = this.textResultRequestOptions();
		this.handleUnauthorized(subject, this.http.post<string>(url, body, requestOptions).pipe(timeout(timeoutMs)));

		return subject.asObservable();
	}

	public postNoResult(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<boolean> {
		let subject = new Subject<boolean>();

		var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.post(url, body, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: Response) => true));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public postObjSer<T extends ObjectSerializer>(url: string, body: any, ctor: new () => T, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

		var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.post(url, body, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: T) => this.extractObjSerData(res, ctor)));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public put<T>(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

        var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.put<T>(url, body, requestOptions).pipe(timeout(timeoutMs)));

		return subject.asObservable();
	}

	public putNoResult(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<boolean> {
		let subject = new Subject<boolean>();

		var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.put(url, body, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: Response) => true));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public putObjSer<T extends ObjectSerializer>(url: string, body: any, ctor: new () => T, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

        var requestOptions = this.defaultRequestOptions();
		this.handleUnauthorized(subject, this.http.put(url, body, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: T) => this.extractObjSerData(res, ctor)));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public delete<T>(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

        var requestOptions = this.defaultRequestOptions();
		requestOptions["body"] = body;
		this.handleUnauthorized(subject, this.http.request<T>('delete', url, requestOptions).pipe(timeout(timeoutMs)));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public deleteNoResult(url: string, body: any, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<boolean> {
		let subject = new Subject<boolean>();

		var requestOptions = this.defaultRequestOptions();
		requestOptions["body"] = body;
		this.handleUnauthorized(subject, this.http.request('delete', url, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: Response) => true));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	public deleteObjSer<T extends ObjectSerializer>(url: string, body: any, ctor: new () => T, timeoutMs: number = UvExplorerClient.DefaultHttpRequestTimeoutMs): Observable<T> {
		let subject = new Subject<T>();

		var requestOptions = this.defaultRequestOptions();
		requestOptions["body"] = body;
		this.handleUnauthorized(subject, this.http.request<T>('delete', url, requestOptions)
			.pipe(timeout(timeoutMs))
			.map((res: T) => this.extractObjSerData(res, ctor)));
			//.catch(this.handleError)

		return subject.asObservable();
	}

	private extractObjSerData<T extends ObjectSerializer>(body: T, ctor: new () => T) {
		let value = new ctor();
		let context = {};
		try {
			value.Read(body, context);
		}
		catch (error) {
			console.error(error);
		}
		return value;
	}

	private extractBinaryData(res: Response) {
		if (res)
			return res.blob();
		else
			return undefined;
	}

	private extractTextData(res: Response) {
		if (res)
			return res.text();
		else
			return undefined;
	}

	private handleError(error: Response | any) {
		let errMsg: string = this.makeErrorMessage(error);
		return Observable.throw(errMsg);
	}

	private makeErrorMessage(error: Response | any): string {
		let errMsg: string;
		if (error instanceof Response) {
			errMsg = `${error.status} - ${error.statusText || ''}`;
		} else {
			errMsg = error.message ? error.message : error.toString();
		}
		//console.error(errMsg);
		return errMsg;
	}

    private defaultRequestOptions() {

        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                "Accept": 'application/json',
			}),
			withCredentials: true,
        }

        return httpOptions;
    }

    private binaryRequestOptions() {
        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
			}),
			withCredentials: true,
            responseType: 'blob' as 'json',
        }

        return httpOptions;
    }

    private textResultRequestOptions() {
        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
			}),
			withCredentials: true,
            responseType: 'text' as 'json',
        }

        return httpOptions;
    }
}
